'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';
import LocationBrowser, { LocationSelection, formatLocationSelection } from '@/components/LocationBrowser';
import InteractiveMap, { MapMarker } from '@/components/InteractiveMap';

interface Guide {
  id: string;
  user_id: string;
  experience: number;
  languages: string[];
  specialties: string[];
  daily_rate: number;
  average_rating: number;
  reviews_count: number;
  trips_completed: number;
  verified: string;
  badge: string;
  category: string;
  province: string;
  location: string;
  availability: boolean;
  users?: { full_name: string; profile_photo: string | null };
}

const fallbackGuides: Guide[] = [
  { id: 'ram-sherpa', user_id: '', experience: 12, languages: ['English', 'Nepali', 'Hindi'], specialties: ['Everest Trekking', 'High Altitude', 'Cultural Tours'], daily_rate: 3500, average_rating: 4.97, reviews_count: 184, trips_completed: 312, verified: 'verified', badge: 'Top Guide', category: 'Trekking', province: 'Koshi', location: 'Solukhumbu', availability: true, users: { full_name: 'Ram Sherpa', profile_photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80' } },
  { id: 'sita-tamang', user_id: '', experience: 8, languages: ['English', 'Nepali', 'French'], specialties: ['Annapurna Trek', 'Cultural Tours', 'Photography'], daily_rate: 3000, average_rating: 4.95, reviews_count: 127, trips_completed: 198, verified: 'verified', badge: 'Cultural Expert', category: 'Cultural', province: 'Gandaki', location: 'Pokhara', availability: true, users: { full_name: 'Sita Tamang', profile_photo: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&q=80' } },
  { id: 'bikash-rai', user_id: '', experience: 10, languages: ['English', 'Nepali', 'German'], specialties: ['Wildlife Safari', 'Jungle Trek', 'Bird Watching'], daily_rate: 2800, average_rating: 4.92, reviews_count: 98, trips_completed: 156, verified: 'verified', badge: 'Wildlife Expert', category: 'Wildlife', province: 'Bagmati', location: 'Chitwan', availability: true, users: { full_name: 'Bikash Rai', profile_photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&q=80' } },
  { id: 'maya-gurung', user_id: '', experience: 15, languages: ['English', 'Nepali', 'Tibetan'], specialties: ['Mustang Trek', 'Buddhist Culture', 'Remote Areas'], daily_rate: 4000, average_rating: 4.98, reviews_count: 203, trips_completed: 287, verified: 'verified', badge: 'Senior Guide', category: 'Trekking', province: 'Gandaki', location: 'Mustang', availability: true, users: { full_name: 'Maya Gurung', profile_photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&q=80' } },
];

const guidePhotos: Record<string, string> = {
  'Ram Sherpa': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80',
  'Sita Tamang': 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&q=80',
  'Bikash Rai': 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&q=80',
  'Maya Gurung': 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&q=80',
};

const categories = ['All', 'Trekking', 'Cultural', 'Wildlife', 'Religious', 'Adventure'];
const provinces = ['All Provinces', 'Koshi', 'Gandaki', 'Bagmati', 'Lumbini', 'Karnali'];
const sortOptions = ['Highest Rated', 'Most Experienced', 'Lowest Price', 'Highest Price', 'Most Booked'];

const GUIDE_COORDS: Record<string, { lat: number; lng: number }> = {
  'solukhumbu': { lat: 27.8000, lng: 86.7000 },
  'pokhara': { lat: 28.2096, lng: 83.9856 },
  'chitwan': { lat: 27.5291, lng: 84.3542 },
  'mustang': { lat: 28.9965, lng: 83.8500 },
  'kathmandu': { lat: 27.7172, lng: 85.3240 },
  'langtang': { lat: 28.2140, lng: 85.5140 },
};

export default function GuidesPage() {
  const [allGuides, setAllGuides] = useState<Guide[]>(fallbackGuides);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('Highest Rated');
  const [maxPrice, setMaxPrice] = useState(10000);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [locationSel, setLocationSel] = useState<LocationSelection>({});
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('guides')
      .select('*, users!guides_user_id_fkey(full_name, profile_photo)')
      .order('average_rating', { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          console.warn('[YatraNest Guides] Query failed:', error.message);
        }
        if (!error && data && data.length > 0) {
          setAllGuides(data);
        }
        // else keep fallbackGuides already set as default state
        setLoading(false);
      });
  }, []);

  const getPhoto = (guide: Guide) => {
    if (guide?.users?.profile_photo) return guide.users.profile_photo;
    const name = guide?.users?.full_name || '';
    return guidePhotos[name] || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80';
  };

  const getName = (guide: Guide) => guide?.users?.full_name || 'Guide';

  const locationLabel = formatLocationSelection(locationSel);

  const sorted = [...allGuides].sort((a, b) => {
    if (sortBy === 'Highest Rated') return (b?.average_rating || 0) - (a?.average_rating || 0);
    if (sortBy === 'Most Experienced') return (b?.experience || 0) - (a?.experience || 0);
    if (sortBy === 'Lowest Price') return (a?.daily_rate || 0) - (b?.daily_rate || 0);
    if (sortBy === 'Highest Price') return (b?.daily_rate || 0) - (a?.daily_rate || 0);
    if (sortBy === 'Most Booked') return (b?.trips_completed || 0) - (a?.trips_completed || 0);
    return 0;
  });

  const filtered = sorted?.filter((g) => {
    if (selectedCategory !== 'All' && g?.category !== selectedCategory) return false;
    if (g?.daily_rate > maxPrice) return false;
    if (verifiedOnly && g?.verified !== 'verified') return false;
    if (availableOnly && !g?.availability) return false;
    // Location filter
    if (locationSel.province && g?.province && g.province !== locationSel.province.name) return false;
    if (locationSel.district && g?.location && !g.location.toLowerCase().includes(locationSel.district.name.toLowerCase())) return false;
    return true;
  });

  const mapMarkers: MapMarker[] = filtered.map(g => {
    const locKey = Object.keys(GUIDE_COORDS).find(k => g.location?.toLowerCase().includes(k));
    const coords = locKey ? GUIDE_COORDS[locKey] : { lat: 28.3949 + (Math.random() - 0.5) * 1.5, lng: 84.124 + (Math.random() - 0.5) * 1.5 };
    return {
      id: g.id,
      lat: coords.lat,
      lng: coords.lng,
      title: getName(g),
      description: `${g.specialties?.slice(0, 2).join(', ')} · ⭐ ${g.average_rating}`,
      category: 'guide',
      price: g.daily_rate ? `NPR ${g.daily_rate.toLocaleString()}/day` : undefined,
      rating: g.average_rating,
      image: getPhoto(g),
      link: `/guides/${g.id}`,
    };
  });

  return (
    <main className="min-h-screen bg-navy">
      <Header />
      {/* Page header */}
      <div className="pt-24 pb-10 px-4" style={{ background: 'linear-gradient(180deg, #0E3570 0%, #0B2D5B 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="section-eyebrow mb-2">Find Your Perfect Guide</div>
          <h1 className="heading-display text-3xl md:text-5xl text-snow mb-3">
            Local Guide <span className="italic text-gold">Marketplace</span>
          </h1>
          <p className="text-snow/60 text-sm max-w-xl">
            Browse {allGuides?.length}+ government-verified local guides across Nepal. Filter by destination, language, specialty, and budget.
          </p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Map toggle button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowMap(m => !m)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-all ${showMap ? 'bg-gold/20 border-gold/50 text-gold' : 'bg-white/5 border-white/10 text-snow/70 hover:text-snow hover:border-gold/30'}`}
          >
            🗺️ {showMap ? 'Hide Map' : 'Map View'}
          </button>
        </div>

        {/* Interactive Map */}
        {showMap && (
          <div className="yn-card rounded-2xl p-4 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-gold text-lg">🗺️</span>
              <h2 className="text-snow font-bold text-lg">Guides Map</h2>
              <span className="text-snow/40 text-xs ml-auto">Satellite · Traffic · Route Planning · Nearby Search</span>
            </div>
            <InteractiveMap
              markers={mapMarkers}
              centerLat={28.3949}
              centerLng={84.124}
              zoom={7}
              height="460px"
              showControls
              showNearbySearch
              showRoutePlanning
            />
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar filters */}
          <aside className="lg:w-72 shrink-0">
            <div className="yn-card rounded-2xl p-5 sticky top-24">
              <h3 className="text-snow font-semibold text-sm mb-5 flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                Filters
              </h3>

              {/* Category */}
              <div className="mb-5">
                <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-2 block">Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories?.map((c) => (
                    <button
                      key={c}
                      onClick={() => setSelectedCategory(c)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        selectedCategory === c
                          ? 'bg-gold text-navy border-gold font-semibold' :'border-white/15 text-snow/60 hover:border-gold/40 hover:text-snow'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Province */}
              <div className="mb-5">
                <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-2 block">Province</label>
                <LocationBrowser
                  value={locationSel}
                  onChange={setLocationSel}
                  showProvince
                  showDistrict
                  showMap={false}
                  placeholder="Select location..."
                  className="yn-select w-full px-3 py-2.5 rounded-xl text-sm"
                />
              </div>

              {/* Max daily rate */}
              <div className="mb-5">
                <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-2 block">
                  Max Daily Rate: <span className="text-gold">Rs. {maxPrice?.toLocaleString()}</span>
                </label>
                <input
                  type="range"
                  min={1000}
                  max={10000}
                  step={500}
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(Number(e?.target?.value))}
                  className="w-full accent-gold"
                />
                <div className="flex justify-between text-xs text-snow/30 mt-1">
                  <span>Rs. 1,000</span>
                  <span>Rs. 10,000</span>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3">
                {[
                  { label: 'Verified Guides Only', value: verifiedOnly, set: setVerifiedOnly },
                  { label: 'Available Now', value: availableOnly, set: setAvailableOnly },
                ]?.map((toggle) => (
                  <label key={toggle?.label} className="flex items-center justify-between cursor-pointer">
                    <span className="text-snow/70 text-sm">{toggle?.label}</span>
                    <div
                      onClick={() => toggle?.set(!toggle?.value)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${toggle?.value ? 'bg-gold' : 'bg-white/10'}`}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${toggle?.value ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </aside>

          {/* Guide grid */}
          <div className="flex-1">
            {/* Sort bar */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-snow/50 text-sm">
                {loading ? 'Loading guides...' : `${filtered?.length} guides found`}
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="yn-select px-3 py-2 rounded-xl text-sm"
              >
                {sortOptions?.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {[1,2,3,4,5,6].map((i) => (
                  <div key={i} className="yn-card rounded-2xl overflow-hidden animate-pulse">
                    <div className="h-28 bg-white/5" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-white/5 rounded w-3/4" />
                      <div className="h-3 bg-white/5 rounded w-1/2" />
                      <div className="h-3 bg-white/5 rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered?.map((guide) => (
                  <div key={guide?.id} className="guide-card rounded-2xl overflow-hidden">
                    <div className="relative h-28 bg-gradient-to-br from-navy-light to-navy-mid">
                      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #D4A017 0%, transparent 60%)' }} />
                      <div className="absolute top-3 right-3">
                        <span className="badge-gold text-[10px] font-semibold px-2 py-1 rounded-full">{guide?.badge}</span>
                      </div>
                      {!guide?.availability && (
                        <div className="absolute top-3 left-3">
                          <span className="bg-white/10 text-snow/60 text-[10px] px-2 py-1 rounded-full">Unavailable</span>
                        </div>
                      )}
                    </div>

                    <div className="px-4 -mt-8 relative">
                      <div className="relative w-14 h-14 rounded-xl overflow-hidden border-2 border-gold/50 shadow-lg">
                        <AppImage src={getPhoto(guide)} alt={`${getName(guide)} guide profile`} fill className="object-cover" />
                      </div>
                      {guide?.verified === 'verified' && (
                        <div className="absolute top-0 left-10 w-5 h-5 bg-sky rounded-full flex items-center justify-center border-2 border-navy">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="p-4 pt-2">
                      <h3 className="text-snow font-semibold">{getName(guide)}</h3>
                      <div className="text-snow/50 text-xs mb-2">{guide?.location}, {guide?.province}</div>

                      <div className="flex items-center gap-1.5 mb-2">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="#D4A017">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                        <span className="text-gold text-xs font-bold">{guide?.average_rating}</span>
                        <span className="text-snow/40 text-xs">({guide?.reviews_count}) · {guide?.trips_completed} trips</span>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-2">
                        {guide?.languages?.slice(0, 2)?.map((l) => (
                          <span key={l} className="text-[10px] text-sky/80 bg-sky/10 border border-sky/20 px-1.5 py-0.5 rounded-full">{l}</span>
                        ))}
                        {(guide?.languages?.length || 0) > 2 && (
                          <span className="text-[10px] text-snow/40 px-1.5 py-0.5">+{(guide?.languages?.length || 0) - 2}</span>
                        )}
                      </div>

                      <div className="text-snow/40 text-xs mb-3">{guide?.experience} yrs experience</div>

                      <div className="flex items-center justify-between pt-3 border-t border-white/5">
                        <div>
                          <span className="text-gold font-bold text-sm">Rs. {guide?.daily_rate?.toLocaleString()}</span>
                          <span className="text-snow/40 text-xs"> /day</span>
                        </div>
                        <div className="flex gap-2">
                          <button className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-snow/50 hover:text-gold hover:border-gold/30 transition-colors">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                          </button>
                          <Link href={`/guides/${guide?.id}`} className="btn-gold text-xs px-3 py-1.5 rounded-lg">
                            View
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && filtered?.length === 0 && (
              <div className="text-center py-20">
                <div className="text-snow/30 text-4xl mb-4">🔍</div>
                <h3 className="text-snow/60 font-semibold mb-2">No guides found</h3>
                <p className="text-snow/30 text-sm">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
